import {
  Badge,
  Button,
  Calendar,
  Col,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faLocationDot,
  faLink,
} from "@fortawesome/free-solid-svg-icons";
import { getCalendarEventsList } from "../repository/data.repository";
import { CalendarEventListItem, MAX_PAGE_SIZE } from "../repository/data.default";
import { getProblemMessage, retry } from "../utils/http";
import { QueryOperation } from "../code/search-query/search-query";

// PD-02 §6.8 — company Calendar surface. Lists events for the current month,
// paints them on an antd Calendar grid as teal badges, and lets the operator
// click a day to see every event scheduled there. New events are created via
// the dedicated Create Event action (actions/create-events) — the "+ New
// Event" button here deep-links there.
function CalendarPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState<Dayjs>(dayjs());
  const [events, setEvents] = useState<CalendarEventListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Dayjs | null>(null);

  useEffect(() => {
    const start = month.startOf("month").startOf("day").toISOString();
    const end = month.endOf("month").endOf("day").toISOString();
    setLoading(true);
    retry(() =>
      getCalendarEventsList({
        searchQuery: {
          conditions: [
            {
              field: "startDateTime",
              operation: QueryOperation.Between,
              values: [start, end],
            },
          ],
        },
        pageNumber: 0,
        pageSize: MAX_PAGE_SIZE,
      }),
    )
      .then((result) => setEvents(result.data))
      .catch((err) => message.error(getProblemMessage(err) || "Failed to load events."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month.year(), month.month()]);

  const eventsByDate = events.reduce<Record<string, CalendarEventListItem[]>>(
    (acc, event) => {
      const key = dayjs(event.startDateTime).format("YYYY-MM-DD");
      (acc[key] = acc[key] ?? []).push(event);
      return acc;
    },
    {},
  );

  function dateCellRender(value: Dayjs) {
    const key = value.format("YYYY-MM-DD");
    const dayEvents = eventsByDate[key] ?? [];
    if (dayEvents.length === 0) return null;
    return (
      <ul className="m-0 list-none p-0">
        {dayEvents.slice(0, 3).map((e) => (
          <li key={e.uid} className="truncate text-xs">
            <Badge color="#14a098" text={e.title} />
          </li>
        ))}
        {dayEvents.length > 3 && (
          <li className="text-xs">
            <Typography.Text type="secondary">
              +{dayEvents.length - 3} more
            </Typography.Text>
          </li>
        )}
      </ul>
    );
  }

  const selectedDayEvents = selected
    ? eventsByDate[selected.format("YYYY-MM-DD")] ?? []
    : [];

  return (
    <Col className="flex flex-1 flex-col overflow-hidden p-4">
      <Row className="mb-4 flex-row items-center justify-between">
        <Typography.Title level={3} className="!m-0">
          Calendar
        </Typography.Title>
        <Space>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faCalendarPlus} />}
            onClick={() => navigate("/actions/create-events")}
          >
            New Event
          </Button>
        </Space>
      </Row>

      <Spin spinning={loading}>
        <Calendar
          cellRender={(value, info) =>
            info.type === "date" ? dateCellRender(value) : info.originNode
          }
          onPanelChange={(value) => setMonth(value)}
          onSelect={(value, info) => {
            if (info.source === "date") setSelected(value);
          }}
        />
      </Spin>

      <Modal
        open={!!selected}
        title={selected?.format("dddd, D MMMM YYYY")}
        footer={<Button onClick={() => setSelected(null)}>Close</Button>}
        onCancel={() => setSelected(null)}
        width={640}
      >
        {selectedDayEvents.length === 0 ? (
          <Typography.Text type="secondary">
            No events scheduled on this day.
          </Typography.Text>
        ) : (
          <Space direction="vertical" size={16} className="w-full">
            {selectedDayEvents.map((e) => (
              <Col
                key={e.uid}
                className="flex flex-col gap-1 rounded p-3"
                style={{ background: "rgba(20,160,152,0.07)" }}
              >
                <Row align="middle" justify="space-between">
                  <Typography.Text strong>{e.title}</Typography.Text>
                  <Tag color="cyan">
                    {dayjs(e.startDateTime).format("HH:mm")}
                    {" – "}
                    {dayjs(e.endDateTime).format("HH:mm")}
                  </Tag>
                </Row>
                {e.description && (
                  <Typography.Text type="secondary">{e.description}</Typography.Text>
                )}
                {e.location && (
                  <Space size={6}>
                    <FontAwesomeIcon icon={faLocationDot} />
                    <Typography.Text>{e.location}</Typography.Text>
                  </Space>
                )}
                {e.onlineLink && (
                  <Space size={6}>
                    <FontAwesomeIcon icon={faLink} />
                    <a href={e.onlineLink} target="_blank" rel="noreferrer">
                      {e.onlineLink}
                    </a>
                  </Space>
                )}
              </Col>
            ))}
          </Space>
        )}
      </Modal>
    </Col>
  );
}

export default CalendarPage;
